import ReactEcharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'

import {
  BasicSelectionBox,
  BasicTable,
  ContentCard,
} from '@/components/customComponents'

import {
  barChartOptions,
  pieChartOptions,
  scatterChartOptions,
} from '@/constants/CHART_CONSTANTS'
import {
  useOptimizations,
  useOverview,
  usePriceCompliance,
  useSkuBreakdown,
} from '@/hooks'
import { MOCK_ATTRIBUTE_SPLIT_DATA_1 } from '@/screens/clarityLens/spendLens/CONSTANTS'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { dateToString } from '@/utils'
import { useEffect, useState } from 'react'

interface OverviewTabProps {
  itemName: string
  clarityLensType?: string
}

export const OverviewTab = ({
  itemName,
  clarityLensType = 'Spend Lens',
}: OverviewTabProps) => {
  const { t } = useTranslation()
  const { selectedBranch, date } = useReduxBaseFilter('spend-lens-item')
  const refreshTrigger = useAppSelector(
    selectPageRefreshTrigger('spend-lens-item')
  )

  const {
    supplierMixChartData,
    manufacturerBreakdownChartData,
    loading: overviewLoading,
    refetch: overviewRefetch,
  } = useOverview(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName
  )

  // SKU Breakdown Tab components
  const [attributeSplitFilter, setAttributeSplitFilter] =
    useState<string>('baristaVsRegular')

  const [attributeSplitData, setAttributeSplitData] = useState<{
    record: Record<string, number>
    labels: string[]
  }>({
    record: {},
    labels: [],
  })

  useEffect(() => {
    setAttributeSplitData(MOCK_ATTRIBUTE_SPLIT_DATA_1)
  }, [])

  const {
    detailedInformationData,
    detailedInformationColumns,
    loading,
    refetch: skuBreakdownRefetch,
  } = useSkuBreakdown(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName
  )

  const {
    optimizationsChartData,
    loading: optimizationsLoading,
    refetch: optimizationsRefetch,
  } = useOptimizations(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName
  )

  const {
    priceComplianceChartData,
    loading: priceComplianceLoading,
    refetch: priceComplianceRefetch,
  } = usePriceCompliance(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName
  )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing overview data...')
      overviewRefetch()
      skuBreakdownRefetch()
      optimizationsRefetch()
      priceComplianceRefetch()
    }
  }, [
    refreshTrigger,
    overviewRefetch,
    skuBreakdownRefetch,
    optimizationsRefetch,
    priceComplianceRefetch,
  ])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* <DetailedNotificationBox
          hierarchy={{
            clarityLens: clarityLensType,
            item: itemName,
          }}
        /> */}
        <ContentCard
          title={t('clarityLens.spendLens.charts.optimizations')}
          loading={optimizationsLoading}
          isEmpty={!optimizationsChartData}
        >
          {optimizationsChartData && (
            <ReactEcharts
              option={getScatterChartData(optimizationsChartData.data)}
            />
          )}
        </ContentCard>
        <ContentCard
          title={t('clarityLens.spendLens.charts.supplierMix')}
          loading={overviewLoading}
          isEmpty={!supplierMixChartData}
        >
          {supplierMixChartData && (
            <ReactEcharts
              option={getPieChartData(
                supplierMixChartData.data,
                supplierMixChartData.labels,
                'money'
              )}
            />
          )}
        </ContentCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ContentCard
          title={t('clarityLens.spendLens.charts.manufacturerBreakdown')}
          loading={overviewLoading}
          isEmpty={!manufacturerBreakdownChartData}
        >
          {manufacturerBreakdownChartData && (
            <ReactEcharts
              option={getPieChartData(
                manufacturerBreakdownChartData.data,
                manufacturerBreakdownChartData.labels,
                'money'
              )}
            />
          )}
        </ContentCard>
        <ContentCard
          title={t('clarityLens.spendLens.charts.priceImpact')}
          loading={priceComplianceLoading}
          isEmpty={!priceComplianceChartData}
        >
          {priceComplianceChartData && (
            <ReactEcharts
              option={getBarChartData(priceComplianceChartData.data)}
            />
          )}
        </ContentCard>
        <ContentCard
          title={t('clarityLens.spendLens.charts.attributeSplit')}
          loading={!attributeSplitData}
          customButton={
            <BasicSelectionBox
              options={[
                { value: 'baristaVsRegular', label: 'Barista vs Regular' },
                { value: 'organic', label: 'Organic' },
                { value: 'countryOfOrigin', label: 'Country of Origin' },
                { value: 'packaging', label: 'Packaging' },
                { value: 'esg', label: 'ESG' },
              ]}
              onValueChange={setAttributeSplitFilter}
              defaultValue={attributeSplitFilter || 'baristaVsRegular'}
              className="w-[200px]"
            />
          }
          isBeta
        >
          {attributeSplitData && (
            <ReactEcharts
              option={getPieChartData(
                attributeSplitData.record,
                attributeSplitData.labels
              )}
            />
          )}
        </ContentCard>
      </div>

      <div>
        {/* TODO: do a badge component for status also update column logic for it */}
        <BasicTable
          title={t('clarityLens.spendLens.charts.detailedInformation')}
          data={detailedInformationData}
          columns={detailedInformationColumns}
          loading={loading}
          enablePagination
        />
      </div>
    </div>
  )
}

const getPieChartData = (
  dataObj: Record<string, number>,
  labels: string[],
  type?: 'percentage' | 'money'
) => {
  const data = labels.map((label) => dataObj[label] ?? 0)
  if (type) {
    return pieChartOptions(data, labels, type)
  }
  return pieChartOptions(data, labels)
}

const getBarChartData = (storeData: { name: string; value: number }[]) => {
  const rawData = [storeData.map((item) => item.value)]
  const seriesNames = ['Sales']
  const xAxisNames = storeData.map((item) => item.name)

  return barChartOptions(rawData, seriesNames, xAxisNames, 'money', true)
}

const getScatterChartData = (
  storeData: {
    name: string
    x: number
    y: number
    info: string
  }[]
) => {
  const uom = storeData.length > 0 && storeData[0].info ? storeData[0].info : ''
  return scatterChartOptions(
    storeData,
    { name: 'Price', type: 'money' },
    { name: `Quantity${uom ? ` (${uom})` : ''}`, type: 'number' }
  )
}
